<script setup>
import {computed, nextTick, onMounted, provide, reactive, ref, toRaw, watch} from "vue";
import {useRoute, useRouter} from "vue-router";
import {useMainOrdersStore} from "@/stores/mainOrders.js";
import {useConfigStore} from "@/stores/config.js";
import {useConfigData} from "@/composables/useConfigData.js";
import HotOrders from "@/views/orders/Details/hotOrders/HotOrders.vue";
import FloatLabel from "primevue/floatlabel";
import {Button, InputGroup, InputGroupAddon, InputText, Textarea} from "primevue";
import Select from "primevue/select";
import ShopStatus from "@/views/orders/Shared/ShopState.vue";
import StateField from "@/views/orders/Shared/StateField.vue";
import {useUserStore} from "@/stores/user.js";
import Attachments from "@/views/orders/Details/Attachments.vue";
import {numeric, required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";
import {Icon} from "@iconify/vue";
import {gregorianDate} from "@/utils/gregorianDate.js";
import {Tab, TabGroup, TabList, TabPanel, TabPanels} from "@headlessui/vue";
import Tooltip from "@/components/Tooltip/index.vue";
import showAlert from "@/utils/showAlert.js";
import {showMessage} from "@/utils/showMessage.js";
import Loading from "@/components/Icon/Loading.vue";
import Logs from "@/views/orders/Details/Logs.vue";
import {useCustomerStore} from "@/stores/customer.js";
import CustomerProfile from "@/components/CustomerProfile/index.vue";
import Customers from "@/components/Customers.vue";

/* tabs */
const tabs = [
  {
    title: "Details",
  },
  {
    title: "Attachments",
  },
];
const selectedTab = ref(0)

/* attachments */
const attachments = ref(null)


/* ---------- Initial order values (to retrieve) ---------- */
const initialGame = ref('')
const initialServer = ref('')
const initialAccount = ref('')

/* ---------- Stores ---------- */
const ordersStore = useMainOrdersStore()
const configStore = useConfigStore()
const userStore = useUserStore();
const customerStore = useCustomerStore()
const route = useRoute()
const router = useRouter()

/* ---------- Loading status ---------- */
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const isUploadingAttachment = ref(false)
const orderNotFound = ref(false)

/* ---------- Initial version of the order for comparison ---------- */
let initialOrder = {}   // Simple object (non-reactive)

/* ---------- creation time of order ---------- */
let creationTime = 0

/* ---------- related to hot orders ---------- */
const hotOrders = ref([]);

/* ---------- related to hot shopState ---------- */
let shopState = '';

/* ---------- Order object ---------- */
const order = reactive({
  state: '',
  store: '',
  game: '',
  server: '',
  account: '',
  title: '',
  amount: '',
  g2gSellID: '',
  link: '',
  price: '',
  takersCut: '',
  tax: '',
  commissionFee: '',
  withdrawalFee: '',
  income: '',
  revenue: '',
  customer: '',
  customerID: 0,
  takers: '',
  notes: '',
  attachments: {},
  id: '',
  uuid: '',
  logs: '',
  finalized: null
})

const rules = computed(() => {
  return {
    state: {required},
    store: {required},
    game: {required},
    server: {required},
    account: {required},
    title: {required},
    amount: {required, numeric},
    price: {required, numeric},
    tax: {required, numeric},
    customerID: {required}
  };
});
const $vulidate = useVuelidate(rules, order);

const fetchingCustomerData = ref(false)
const selectedCustomer = ref({})
const isShowCustomerProfileModal = ref(false);
const canChangeCustomer = ref(false)
const canShowCustomerProfile = computed(() => userStore.hasPermission('BIG_BOSS') || userStore.hasPermission('ADMIN'))

/* Tab content height */
const detailsContent = ref()
const tabHeight = ref(0)

/* ---------- Select options ---------- */
const {stores, games} = useConfigData('Currency')

/* ---------- Selected store ---------- */
const selectedStoreObj = computed(() => stores.value.find(a => a.store === order.store) || null)

const selectedGame = computed(() => games.value.find(g => g.game === order.game) || null)

const servers = computed(() => {
  if (!selectedGame.value) return []

  const servers = selectedGame.value.servers

  if (!servers.includes('All')) {
    return servers
  }

  return [
    'All',
    ...servers.filter(server => server !== 'All')
  ]
})

const accounts = computed(() => {
  return order.game ? ordersStore.getGameAccountByGame(order.game) || [] : []
})

const canUpdateOrder = computed(() => userStore.hasPermission("ORDERS_ITEM_UPDATE") || userStore.hasPermission('BIG_BOSS'))
const canRemoveOrder = computed(() => userStore.hasPermission('ORDERS_ITEM_REMOVE') || userStore.hasPermission('BIG_BOSS'))
const canFinalizedOrder = computed(() => userStore.hasPermission('FINALIZE_ORDERS') || userStore.hasPermission('BIG_BOSS'))

const canBeDelivered = computed(() => {
  return hotOrders.value.every(order =>
      ['Delivered', 'Canceled', 'Paid'].includes(order.state)
  )
})
const canBePaidOrCanceled = computed(() => {
  return hotOrders.value.every(order =>
      ['Canceled', 'Paid'].includes(order.state)
  )
})

/* ---------- Selected account and its title ---------- */
const selectedAccountObj = computed(() => accounts.value.find(a => a.id === order.account) || null)

const accountTitle = computed(() => selectedAccountObj.value ? selectedAccountObj.value.title : '')

/* ---------- Reaction to Game Change ---------- */
function onGameChange() {
  /* ---------- 3‑a) If the same initial Game is selected ---------- */
  if (order.game === initialGame.value) {
    // Server recovery
    if (servers.value.includes(initialServer.value)) {
      order.server = initialServer.value
    } else if (servers.value.length === 1) {
      order.server = servers.value[0]
    } else {
      order.server = ''
    }

    // Account recovery
    if (accounts.value.some(a => a.id === initialAccount.value)) {
      order.account = initialAccount.value
    } else if (accounts.value.length === 1) {
      order.account = accounts.value[0].id
    } else {
      order.account = ''
    }
    return
  }

  /* ---------- 3‑b) New Game (non-primary) ---------- */
  order.server =
      servers.value.length === 1 ? servers.value[0] : ''
  order.account =
      accounts.value.length === 1 ? accounts.value[0].id : ''
}

const fetchInitialData = async () => {
  loading.value = true

  await ordersStore.action_getGameAccounts()

  const data = await ordersStore.action_get_order('Item', route.params.id)
  if (data === false) {
    orderNotFound.value = true;
    loading.value = false
    return
  }
  for (const key in order) {
    if (data[key] !== undefined) {
      order[key] = data[key]
    }
  }
  hotOrders.value = data.hotOrders ?? []
  creationTime = data.creationTime;
  shopState = data.shopState ?? '';

  // Save initial values
  initialGame.value = order.game
  initialServer.value = order.server
  initialAccount.value = order.account
  initialOrder = JSON.parse(JSON.stringify(order))
  if (!order.customer && !order.customerID) {
    canChangeCustomer.value = true
  }
  if (order.customerID) {
    await getCustomer();
  }
  loading.value = false
  await nextTick()
  await updateTabHeight()
}

const getCustomer = async () => {
  fetchingCustomerData.value = true
  try {
    const res2 = await customerStore.action_getCustomer({id: order.customerID})
    if (res2.result === true) {
      selectedCustomer.value = res2.data
    } else {
      //toast.add({severity: 'error', summary: 'Failed', detail: res2, life: 1500});
      //showMessage(res2, 'error');
    }
  } catch (err) {
    //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 1500});
    showMessage(err, 'error');
  } finally {
    fetchingCustomerData.value = false
  }
}

/* ---------- Deep comparison function ---------- */
function diffDeep(curr, base) {
  if (Object.is(curr, base)) return null

  if (
      typeof curr !== 'object' || curr === null ||
      typeof base !== 'object' || base === null
  ) {
    return curr                    // Some primitive or different type
  }

  const out = Array.isArray(curr) ? [] : {}
  let hasDiff = false
  const keys = Object.keys(curr)     // Only current keys (order object)
  keys.forEach(key => {
    const sub = diffDeep(curr[key], base[key])
    if (sub !== null) {
      out[key] = sub
      hasDiff = true
    }
  })

  return hasDiff ? out : null
}

/* ---------- Detect changed fields ---------- */
function getChangedFields() {
  // Use toRaw to output Reactive Proxy
  const rawCurr = toRaw(order)
  const diff = diffDeep(rawCurr, initialOrder) || {}

  // If diff was empty, no changes
  return {id: order.id, ...diff}   // id should always be
}

/* ---------- Save only changed fields ---------- */
async function saveChanges(backToList = true) {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  const changed = getChangedFields()

  if (attachments.value.length) {
    changed.attachments = {
      paymentReceipts: attachments.value
    }
  }

  // If it's just id, we haven't changed anything
  if (Object.keys(changed).length === 1) {
    showMessage('Nothing Changed', 'warning');
    return
  }

  if (order.state === 'Delivered' && !canBeDelivered.value) {
    showMessage('Incompatible Suborder states.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
    return false
  }

  if ((order.state === 'Canceled' || order.state === 'Paid') && !canBePaidOrCanceled.value) {
    showMessage('Incompatible Suborder states.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
    return false
  }

  try {
    saving.value = true
    const finalPayload = (({takersCut, ...rest}) => rest)(changed);
    const res = await ordersStore.updateOrder('Item', finalPayload)

    if (res === true) {
      showMessage('Order Updated', 'success');
      // After success, synchronize the initial version with the current state
      initialOrder = structuredClone(toRaw(order))
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('itemOrders');
      if (backToList) {
        backToOrders();
      }
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    saving.value = false
  }
}

async function onDelete() {
  // User verification
  const ok = await showAlert('You are deleting order', 'Are you sure?', 'Yes', 'No');
  if (!ok) return

  // 4) API call
  try {
    deleting.value = true
    const res = await ordersStore.removeOrder('Item', {id: order.id})

    if (res === true) {
      showMessage('Order Deleted', 'success');
      ordersStore.reset_lastUpdate();
      ordersStore.reset_orders('itemOrders');
      backToOrders();
    } else {
      showMessage(res, 'error');
    }
  } catch (err) {
    showMessage(err, 'error');
  } finally {
    deleting.value = false
  }
}

const stateChange = (state) => {
  if (state === 'Delivered' && !canBeDelivered.value) {
    showMessage('Incompatible Suborder states.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
  }
  if ((state === 'Canceled' || state === 'Paid') && !canBePaidOrCanceled.value) {
    showMessage('Incompatible Suborder states.', 'warning', 2000, false, 'top-center', 'text-slate-700 font-semibold');
  }
}

/* ---------- Income and profit calculations ---------- */
const recalc = () => {
  const price = Number(order.price)
  const tax = Number(order.tax)
  const takersCut = Number(order.takersCut)

  order.commissionFee = selectedStoreObj.value?.commissionFee ?? 0
  order.withdrawalFee = selectedStoreObj.value?.withdrawalFee ?? 0

  /* -------- income -------- */
  if (order.store && price) {
    const incomeNum = ((price * (1 - Number(order.commissionFee))) - tax) * (1 - Number(order.withdrawalFee))
    order.income = incomeNum.toFixed(2)

    const revenueNum = Number(order.income) - takersCut
    order.revenue = revenueNum.toFixed(2)

  } else {
    order.income = ''
  }

  /* -------- revenue -------- */
  /*if (order.income !== '' && order.takersCut !== '' && !isNaN(takersCut)) {
    const revenueNum = Number(order.income) - takersCut
    order.revenue = revenueNum.toFixed(2) // ← رشته
  } else {
    order.revenue = ''
  }*/
}

function validateForm() {
  $vulidate.value.$touch();
  if ($vulidate.value.$invalid) {
    selectedTab.value = 0
    return false;
  }

  if (isUploadingAttachment.value) {
    selectedTab.value = 1
    return false;
  }

  return true
}

const getSelectedCustomer = (customer) => {
  selectedCustomer.value = customer || null
  order.customerID = customer ? customer.id : null
}

function showCustomerProfileModal(user) {
  //if (user.isLoading) return;
  if (canShowCustomerProfile.value) {
    customerStore.setSelectedCustomer(user);
    isShowCustomerProfileModal.value = true
  }
}

const changeFinalized = () => {
  if (order.finalized) {
    order.finalized = false
  } else {
    order.finalized = true
  }
}

/* Tab content height management */
const updateTabHeight = async () => {
  if (selectedTab.value === 0) {
    await nextTick()
    const detailsH = detailsContent.value?.scrollHeight
    tabHeight.value = detailsH + 16
  }
}

// Update tab height when validation errors appear/disappear
watch(() => $vulidate.value.$errors, () => {
  nextTick(() => updateTabHeight())
}, {deep: true})

// Update when order fields change (affects income/revenue display, etc.)
watch(() => order, () => {
  nextTick(() => updateTabHeight())
}, {deep: true})

// Update when hot orders change (they are below details)
watch(() => hotOrders.value, () => {
  nextTick(() => updateTabHeight())
}, {deep: true})

/* ---------- Always stay up to date! ---------- */
watch(() => order.store, () => {
  order.commissionFee = selectedStoreObj.value?.commissionFee ?? 0
  order.withdrawalFee = selectedStoreObj.value?.withdrawalFee ?? 0
  recalc()
})

watch(
    [() => order.price, () => order.takersCut, () => order.tax],
    recalc,
    {immediate: true}          // Immediately after mount and every time it changes
)

/* ---------- Watch for Game Change ---------- */
watch(() => order.game, () => onGameChange())

function backToOrders() {
  router.push({name: 'item-orders'})
}

onMounted(() => fetchInitialData())

provide('isShowCustomerProfileModal', isShowCustomerProfileModal)
</script>

<template>
  <div class="h-full">
    <TabGroup :selectedIndex="selectedTab" @change="(index)=> selectedTab = index">
      <div class="space-y-4 text-white/80 text-sm h-full">
        <!-- Loading mode -->
        <div v-if="loading" class="flex items-center justify-center h-60">
          <Icon icon="eos-icons:bubble-loading" class="size-12"/>
        </div>
        <div v-else-if="orderNotFound" class="flex flex-col gap-2 items-center justify-center h-60 text-lg">
          <svg xmlns="http://www.w3.org/2000/svg" width="2.5rem" height="2.5rem" viewBox="0 0 24 24">
            <path d="M0 0h24v24H0z" fill="none" />
            <g class="alert-outline">
              <g fill="currentColor" class="Vector">
                <path fill-rule="evenodd" d="M22 12c0 5.523-4.477 10-10 10S2 17.523 2 12S6.477 2 12 2s10 4.477 10 10m-10 8a8 8 0 1 0 0-16a8 8 0 0 0 0 16" clip-rule="evenodd" />
                <path fill-rule="evenodd" d="M12 14a1 1 0 0 1-1-1V8a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1" clip-rule="evenodd" />
                <path d="M11 16a1 1 0 1 1 2 0a1 1 0 0 1-2 0" />
              </g>
            </g>
          </svg>
          Order Not Found
        </div>
        <TabPanels v-else class="h-full">
          <div class="flex flex-col gap-2 h-full">
            <div class="flex w-full">
              <div class="grid grid-cols-5 gap-4 h-full w-full">
                <div class="col-span-4 flex flex-row justify-between items-center">
                  <div class="flex flex-row gap-2 items-center">
                    <Tooltip placement="top" arrow>
                      <template #button>
                        <div class="back-btn" @click="backToOrders">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                               class="w-4 h-4 2xl:w-6 2xl:h-6">
                            <path fill="currentColor"
                                  d="m7.825 13l4.9 4.9q.3.3.288.7t-.313.7q-.3.275-.7.288t-.7-.288l-6.6-6.6q-.15-.15-.213-.325T4.426 12t.063-.375t.212-.325l6.6-6.6q.275-.275.688-.275t.712.275q.3.3.3.713t-.3.712L7.825 11H19q.425 0 .713.288T20 12t-.288.713T19 13z"/>
                          </svg>
                        </div>
                      </template>
                      <span>{{ $t('message.back') }}</span>
                    </Tooltip>
                    <TabList class="">
                      <Tab
                          v-slot="{ selected }"
                          as="template"
                          v-for="(item, i) in tabs"
                          :key="i"
                      >
                        <button
                            :class="[
                              selected
                                ? 'text-white bg-primary-700'
                                : 'text-slate-500 bg-white dark:bg-slate-700 dark:text-slate-300',
                            ]"
                            class="text-xs 2xl:text-sm font-medium last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-4 2xl:px-6 py-1 2xl:py-2 transition duration-150 rounded-none first:rounded-l-2xl last:rounded-r-2xl border-r last:border-r-0 border-slate-500"
                        >
                          {{ item.title }}
                        </button>
                      </Tab>
                    </TabList>
                  </div>
                  <div class="flex flex-row items-center gap-2">
                    <div v-if="canFinalizedOrder" @click="changeFinalized"
                         class="flex gap-2 items-center rounded-md h-[32px] 2xl:h-[40px] text-xs 2xl:text-sm px-2 py-1 border cursor-pointer select-none"
                         :class="[order.finalized ? 'bg-primary-700 border-transparent' : 'bg-transparent border-slate-700']">
                      <div class="flex items-center size-4 border rounded-sm"
                           :class="[order.finalized ? 'border-slate-400' : 'border-slate-700']">
                        <svg v-if="order.finalized" xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                             stroke-linecap="round" stroke-linejoin="round"
                             class="lucide lucide-check-icon lucide-check">
                          <path d="M20 6 9 17l-5-5"/>
                        </svg>
                      </div>
                      Finalized
                    </div>
                    <StateField v-model="order.state" type="edit" @update:modelValue="stateChange"></StateField>
                  </div>
                </div>
                <div class="col-span-1 flex flex-row justify-between">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs justify-center pl-3">
                    <span>UUID: {{ order.uuid }}</span>
                    <span>Creation Time: {{ gregorianDate(creationTime, 'DD MMM YYYY HH:mm') }}</span>
                  </div>
                  <div class="flex items-start">
                    <Logs :order="order"/>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex-1 grid grid-cols-5 gap-4 h-full">
              <div class="col-span-4 flex flex-col relative h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex relative overflow-hidden">
                  <TabPanel :unmount="false" :style="{ height: tabHeight ? tabHeight + 'px' : 'auto' }"
                            class="flex-1 flex flex-col gap-4 relative h-full overflow-auto custom-scrollbar-2 pt-4">
                    <div ref="detailsContent" class="grid grid-cols-12 gap-4">
                      <div class="col-span-12 flex flex-row items-start gap-4">
                        <!-- ================== Store ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                          <FloatLabel class="w-full custom-select" :class="{ 'has-error': $vulidate.store.$error }"
                                      variant="on">
                            <Select v-model="order.store" inputId="store" :options="stores" optionLabel="store"
                                    option-value="store"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.store.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    overlayClass="custom-select-overlay"
                                    filter autoFilterFocus/>
                            <label for="store" class="!bg-slate-900">Store</label>
                          </FloatLabel>
                          <template v-if="$vulidate.store.$error">
                            <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.store.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Game ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                          <FloatLabel class="w-full custom-select"
                                      :class="{ 'has-error': $vulidate.game.$error }"
                                      variant="on">
                            <Select v-model="order.game" inputId="game" :options="games" optionLabel="game"
                                    option-value="game"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.game.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    filter autoFilterFocus/>
                            <label for="game" class="!bg-slate-900">Game</label>
                          </FloatLabel>
                          <template v-if="$vulidate.game.$error">
                            <p class="text-red-300 mt-1 text-xs"
                               v-if="!$vulidate.game.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Server ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <FloatLabel class="w-full custom-select"
                                      :class="{ 'has-error': $vulidate.server.$error}"
                                      variant="on">
                            <Select v-model="order.server" inputId="server" :options="servers"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.server.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    :virtualScrollerOptions="{ itemSize: 38 }"
                                    filter autoFilterFocus/>
                            <label for="server" class="!bg-slate-900">Server</label>
                          </FloatLabel>
                          <template v-if="$vulidate.server.$error">
                            <p class="text-red-300 mt-1 text-xs"
                               v-if="!$vulidate.server.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                        <!-- ================== Account ================== -->
                        <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-max">
                          <FloatLabel class="w-full custom-select"
                                      :class="{ 'has-error': $vulidate.account.$error }"
                                      variant="on">
                            <Select v-model="order.account" inputId="account" :options="accounts" optionLabel="title"
                                    option-value="id"
                                    class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm"
                                    :class="[$vulidate.account.$error ? '!border !border-red-300' : '!border-slate-700']"
                                    input-class="!text-xs 2xl:!text-sm"
                                    :virtualScrollerOptions="{ itemSize: 32 }"
                                    filter autoFilterFocus/>
                            <label for="account" class="!bg-slate-900">Account</label>
                          </FloatLabel>
                          <template v-if="$vulidate.account.$error">
                            <p class="text-red-300 mt-1 text-xs"
                               v-if="!$vulidate.account.required.$response">
                              This field is required
                            </p>
                          </template>
                        </div>
                      </div>
                      <div class="col-span-12 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="title" v-model="order.title" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error': $vulidate.title.$error }"/>
                          <label for="title" class="!bg-slate-900"
                                 :class="{ 'focus:text-red-300': $vulidate.title.$error }">Title</label>
                        </FloatLabel>
                        <template v-if="$vulidate.title.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.title.required.$response">
                            This field is required
                          </p>
                        </template>
                      </div>
                      <div class="col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText v-allow-digits-dot id="amount" v-model="order.amount" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'has-error': $vulidate.amount.$error}"/>
                          <label for="amount" class="!bg-slate-900"
                                 :class="{ 'has-error': $vulidate.amount.$error}">Amount</label>
                        </FloatLabel>
                        <template v-if="$vulidate.amount.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.amount.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.amount.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <div class="col-span-3 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="g2gSellID" v-model="order.g2gSellID" autocomplete="off"
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                          <label for="g2gSellID" class="!bg-slate-900">Sell ID</label>
                        </FloatLabel>
                      </div>
                      <div class="col-span-6 flex flex-row gap-2 w-full">
                        <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <InputGroup>
                            <FloatLabel variant="on">
                              <InputText id="link" v-model="order.link" autocomplete="off"
                                         class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"/>
                              <label for="link" class="!bg-slate-900">Link</label>
                            </FloatLabel>
                            <Button class="!bg-transparent !border-slate-700 border-l-0 !text-xs !px-2 !py-0">
                              <a :href="order?.link" target="_blank"
                                 class="text-blue-400 whitespace-nowrap hover:bg-slate-800 px-2 rounded-xl py-1">
                                Open Link
                              </a>
                            </Button>
                          </InputGroup>
                        </div>
                        <ShopStatus :state="shopState"/>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on"
                                      :class="{ 'has-error': $vulidate.price.$error }">
                            <InputText v-allow-digits-dot id="price" v-model="order.price" autocomplete="off"
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{ 'has-error has-addon': $vulidate.price.$error}"/>
                            <label for="price" class="!bg-slate-900"
                                   :class="{ 'has-error': $vulidate.price.$error }">Price</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                        <template v-if="$vulidate.price.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.price.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on">
                            <InputText v-allow-digits-dot id="takersCut" :value="order.takersCut" autocomplete="off"
                                       readonly disabled
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{'p-filled': !!order.takersCut }"/>
                            <label for="takersCut" class="!bg-slate-900">Takers Cut</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                      </div>
                      <div class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <InputGroup>
                          <FloatLabel variant="on" :class="{ 'has-error': $vulidate.tax.$error }">
                            <InputText v-allow-digits-dot id="tax" :value="order.tax" autocomplete="off" readonly
                                       disabled
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                       :class="{ 'has-error has-addon': $vulidate.tax.$error, 'p-filled': !!order.tax }"/>
                            <label for="tax" class="!bg-slate-900"
                                   :class="{ 'has-error': $vulidate.tax.$error }">Tax</label>
                          </FloatLabel>
                          <InputGroupAddon class="!bg-transparent !border-slate-700 h-full">$</InputGroupAddon>
                        </InputGroup>
                        <template v-if="$vulidate.tax.$error">
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.required.$response">
                            This field is required
                          </p>
                          <p class="text-red-300 mt-1 text-xs" v-if="!$vulidate.tax.numeric.$response">
                            This field should be a number
                          </p>
                        </template>
                      </div>
                      <div class="col-span-4 flex flex-col gap-1 text-xs 2xl:text-sm">
                        <div class="flex flex-row gap-2 items-center text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="whitespace-nowrap">Commission Fee:</p>
                          <div v-if="order?.commissionFee" class="w-full">
                            {{
                              order?.commissionFee ? (order.commissionFee) * 100 + ' %' : '&nbsp'
                            }}
                          </div>
                          <div v-else class="w-full">
                            Store not selected
                          </div>
                        </div>
                        <div class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs whitespace-nowrap">Withdrawal Fee:</p>
                          <div v-if="order?.withdrawalFee" class="w-full">
                            {{
                              order?.withdrawalFee ? (order.withdrawalFee) * 100 + ' %' : '&nbsp'
                            }}
                          </div>
                          <div v-else class="w-full">
                            Store not selected
                          </div>
                        </div>
                      </div>
                      <div class="col-span-8 flex flex-col gap-1 text-xs 2xl:text-sm">
                        <div v-if="order?.income" class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs">Income:</p>
                          <div class="w-full">
                            {{ order?.income ? '$ ' + order.income : '&nbsp' }}
                          </div>
                        </div>
                        <div v-if="order?.revenue" class="flex flex-row gap-2 items-start text-xs">
                          <span class="text-[4px]">⬤</span>
                          <p class="text-xs">Revenue:</p>
                          <div class="w-full">
                            {{ order?.revenue ? '$ ' + order.revenue : '&nbsp' }}
                          </div>
                        </div>
                      </div>
                      <template v-if="!canChangeCustomer">
                        <div v-if="order.customerID && !fetchingCustomerData"
                             class="col-span-12 lg:col-span-4 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[40px] 2xl:h-[50px]">
                          <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                               :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                            <div
                                class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="currentColor"
                                      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-4 text-xs 2xl:text-sm">
                            <div class="flex flex-col justify-between">
                              <div class="flex flex-row gap-6">
                                <div class="flex-1 truncate">{{
                                    selectedCustomer.name || selectedCustomer.storeID
                                  }}
                                </div>
                                <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                                </div>
                              </div>
                              <div class="text-slate-400 overflow-hidden text-ellipsis whitespace-nowrap">
                                <span>{{ selectedCustomer.discordUsername ?? "" }}</span>
                                <span>
                                {{
                                    (selectedCustomer.discordDisplayName ? "&nbsp- " + selectedCustomer.discordDisplayName : "")
                                  }}
                              </span>
                              </div>
                            </div>
                            <div class="flex flex-col justify-between">
                              <div>{{ selectedCustomer.verificationState ?? "" }}</div>
                              <div class="truncate">
                                {{
                                  selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                        <div v-else class="col-span-4 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                          <FloatLabel variant="on">
                            <InputText id="customer" :value="order.customer" autocomplete="off" readonly disabled
                                       class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700 p-filled"/>
                            <label for="customer" class="!bg-slate-900">Customer</label>
                          </FloatLabel>
                        </div>
                      </template>
                      <div v-else class="col-span-12 grid grid-cols-12 gap-4">
                        <div class="col-span-4 flex flex-row gap-2 text-xs 2xl:text-sm">
                          <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                            <Customers v-model="selectedCustomer" @update:modelValue="getSelectedCustomer"
                                       appendTo="body"
                                       label="Customer" :required="true"/>
                          </div>
                        </div>
                        <div v-if="selectedCustomer?.id"
                             class="col-span-8 flex flex-row gap-2 w-full items-center border border-slate-700 rounded-md px-2 py-1 h-[32px] 2xl:h-[40px]">
                          <div @click.stop="showCustomerProfileModal(selectedCustomer)"
                               :class="['h-full flex items-center', canShowCustomerProfile ? 'cursor-zoom-in' : 'cursor-default']">
                            <div
                                class="flex items-center justify-center rounded-full border border-slate-700 h-full aspect-square">
                              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                <path fill="currentColor"
                                      d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15c0-1.09 1.01-1.85 2.7-1.85c1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61c0 2.31 1.91 3.46 4.7 4.13c2.5.6 3 1.48 3 2.41c0 .69-.49 1.79-2.7 1.79c-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55c0-2.84-2.43-3.81-4.7-4.4"/>
                              </svg>
                            </div>
                          </div>
                          <div class="flex-1 grid grid-cols-2 gap-1">
                            <div class="flex flex-col">
                              <div class="flex flex-row gap-6 text-xs">
                                <div>{{ selectedCustomer.name || selectedCustomer.storeID }}</div>
                                <div v-if="selectedCustomer.rating" class="flex flex-row items-center gap-0.5">
                                <span class="flex flex-row items-end">
                                  {{ selectedCustomer.rating }}⭐ ({{ selectedCustomer.ratingCount }})
                                </span>
                                </div>
                              </div>
                              <div class="flex flex-row w-max text-xs text-slate-400">
                                <div>{{ selectedCustomer.discordUsername ?? "ــــــ" }}</div>
                                <div>
                                  {{
                                    "&nbsp- " + (selectedCustomer.discordDisplayName ? selectedCustomer.discordDisplayName : "ــــــ")
                                  }}
                                </div>
                              </div>
                            </div>
                            <div class="flex flex-col text-xs">
                              <div>{{ selectedCustomer.verificationState ?? "ــــــ" }}</div>
                              <div>
                                {{
                                  selectedCustomer.registrationTime ? gregorianDate(selectedCustomer.registrationTime, 'DD MMM YYYY - HH:mm') : ''
                                }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="order.takers" class="col-span-8 flex flex-col gap-0.5 text-xs 2xl:text-sm">
                        <FloatLabel variant="on">
                          <InputText id="takers" :value="order.takers" autocomplete="off" readonly disabled
                                     class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                     :class="{ 'p-filled': !!order.takers }"/>
                          <label for="takers" class="!bg-slate-900">Takers</label>
                        </FloatLabel>
                      </div>
                    </div>
                  </TabPanel>
                  <TabPanel :unmount="false" :style="{ height: tabHeight ? tabHeight + 'px' : 'auto' }"
                            class="flex-1 flex flex-col gap-6 relative h-full overflow-auto px-2 custom-scrollbar-2 pt-4">
                    <!-- Only changes -->
                    <Attachments
                        id="editOrder"
                        sizeClass="w-24 h-24"
                        fontSize="text-xs"
                        :count="16"
                        :backend-images="order.attachments?.paymentReceipts ?? []"
                        :only-changes="true"
                        v-model:uploading="isUploadingAttachment"
                        @update:payload="val => (attachments = val)"
                    />
                  </TabPanel>
                </div>
                <HotOrders :items="hotOrders" :order="order" orderType="Item" @refreshOrder="fetchInitialData"
                           :validateForm="validateForm" class="pt-4"/>
              </div>
              <div class="col-span-1 flex flex-col h-full overflow-hidden">
                <hr class="border-0 h-0.5 bg-slate-800">
                <div class="flex flex-col h-full overflow-hidden gap-4 pt-4">
                  <div class="flex-1 flex flex-col gap-0.5 text-xs 2xl:text-sm h-full">
                    <FloatLabel variant="on" class="h-full">
                      <Textarea v-auto-dir id="notes" v-model="order.notes" rows="5" style="resize: none"
                                class="!bg-transparent w-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 h-full"/>
                      <label for="notes" class="!bg-slate-900">Notes</label>
                    </FloatLabel>
                  </div>
                  <hr v-if="canRemoveOrder || canUpdateOrder" class="border-0 h-0.5 bg-slate-800">
                  <div v-if="canRemoveOrder || canUpdateOrder" class="grid gap-2"
                       :class="[canRemoveOrder && canUpdateOrder ? 'grid-cols-2' : 'grid-cols-1']">
                    <button v-if="canRemoveOrder" @click="onDelete"
                            class="btn btn-outline-dark py-2 text-xs 2xl:text-sm">
                      <span v-if="deleting"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                        <Loading/>
                      </span>
                      Delete order
                    </button>
                    <button v-if="canUpdateOrder" @click="saveChanges"
                            class="btn btn-outline-primary py-2 text-xs 2xl:text-sm"
                            :disabled="saving">
                      <span v-if="saving"
                            class="animate-[spin_2s_linear_infinite] inline-block align-middle ltr:mr-2 rtl:ml-2 shrink-0">
                        <Loading/>
                      </span>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabPanels>
      </div>
    </TabGroup>
    <CustomerProfile v-if="isShowCustomerProfileModal" :userID="customerStore.selectedCustomer.id"/>
  </div>
</template>

<style scoped>
.input {
  @apply w-full px-3 py-2 border border-gray-700 rounded-md focus:outline-none focus:ring focus:border-blue-500;
}

.back-btn {
  @apply h-6 2xl:h-8 w-6 2xl:w-8 bg-slate-200 dark:bg-slate-700 dark:text-slate-200 text-slate-600 flex flex-col justify-center items-center text-base rounded-full cursor-pointer;
}
</style>